import { Controller } from "@hotwired/stimulus"
import debounce from "lodash.debounce"

export default class NavigationTabsOverflowController extends Controller {
  static targets = ["navigationTabWrapper", "navigationTab"]
  static outlets = ["dropdown-menu"]

  initialize() {
    this.updateDropdownMenuVisibility = debounce(
      this.#updateDropdownMenuVisibility.bind(this),
      10
    )
  }

  connect() {
    this.resizeObserver = new ResizeObserver(
      this.updateDropdownMenuVisibility.bind(this)
    )
    this.#updateDropdownMenuVisibility()
    this.resizeObserver.observe(this.navigationTabWrapperTarget)
  }

  disconnect() {
    this.resizeObserver.disconnect()
  }

  #updateDropdownMenuVisibility() {
    requestAnimationFrame(() => {
      this.#toggleNavigationTabsVisibility()

      this.#toggleDropdownVisibility()
    })
  }

  #toggleNavigationTabsVisibility() {
    this.#resetNavigationTabsVisibility()

    const availableWidth = this.navigationTabWrapperTarget.offsetWidth
    let currentWidth = 0

    this.#navigationTabs.forEach((tab, index, navigationTabs) => {
      const tabWidth = this.#calculateTabWidth(tab, navigationTabs, index)

      currentWidth += tabWidth

      tab.classList.toggle("hidden", currentWidth > availableWidth)
    })
  }

  #toggleDropdownVisibility() {
    if (this.#shouldDisplayDropdownMenu) {
      this.dropdownMenuOutlet.element.classList.remove("hidden")
      this.#toggleDropdownMenuItemVisibility()
    } else {
      this.dropdownMenuOutlet.element.classList.add("hidden")
    }
  }

  #toggleDropdownMenuItemVisibility() {
    this.#dropdownMenuItems.forEach((item, index) => {
      const hideDropdownItem = !this.#hiddenNavigationTabIndexes.includes(index)

      item.classList.toggle("hidden", hideDropdownItem)
    })
  }

  #resetNavigationTabsVisibility() {
    this.#navigationTabs.forEach((tab) => tab.classList.remove("hidden"))
  }

  #calculateTabWidth(tab, navigationTabs, index) {
    const rect = tab.getBoundingClientRect()
    const tabWidth = rect.width

    if (index > 0) {
      const prevTab = navigationTabs[index - 1]
      const prevRect = prevTab.getBoundingClientRect()
      const gap = Math.max(0, rect.left - prevRect.right)
      return tabWidth + gap
    }

    return tabWidth
  }

  get #shouldDisplayDropdownMenu() {
    return this.#hiddenNavigationTabIndexes.length > 0
  }

  get #hiddenNavigationTabIndexes() {
    return this.#navigationTabs.reduce((hiddenIndexes, tab, index) => {
      if (tab.classList.contains("hidden")) {
        hiddenIndexes.push(index)
      }
      return hiddenIndexes
    }, [])
  }

  get #navigationTabs() {
    return this.navigationTabTargets
  }

  get #dropdownMenuItems() {
    return Array.from(this.dropdownMenuOutlet.menuTarget.children)
  }
}
